import { useEffect, useState } from "react";
import { Fingerprint, X, Sparkles } from "lucide-react";
import { startRegistration, browserSupportsWebAuthn } from "@simplewebauthn/browser";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

/**
 * Auto-pops once per session for signed-in users who don't have a passkey yet.
 * Dismissals are stored per-user-id so they aren't nagged on every visit;
 * a "remind me later" path lets users re-prompt on the next session.
 */
const STORAGE_KEY_PREFIX = "studict_passkey_prompt_v1_";

export default function PasskeyPrompt() {
  const { user, loading } = useAuth();
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loading || !user?.user_id) return;
    if (!browserSupportsWebAuthn()) return;

    const key = `${STORAGE_KEY_PREFIX}${user.user_id}`;
    const stored = localStorage.getItem(key);
    if (stored === "added" || stored === "never") return;

    const check = async () => {
      try {
        const r = await api.get("/webauthn/credentials");
        const count = (r.data?.credentials || []).length;
        if (count === 0) {
          // Give the user a beat to land on dashboard before nagging.
          setTimeout(() => setShow(true), 1200);
        } else {
          localStorage.setItem(key, "added");
        }
      } catch {
        /* silent — user isn't blocked by this */
      }
    };
    check();
  }, [loading, user?.user_id]);

  const close = (reason) => {
    setShow(false);
    if (!user?.user_id) return;
    const key = `${STORAGE_KEY_PREFIX}${user.user_id}`;
    if (reason === "added") localStorage.setItem(key, "added");
    else if (reason === "never") localStorage.setItem(key, "never");
    else localStorage.setItem(key, `snoozed:${Date.now()}`);
  };

  const addPasskey = async () => {
    setBusy(true);
    try {
      const opts = await api.post("/webauthn/register/options");
      const att = await startRegistration({ optionsJSON: opts.data.options });
      await api.post("/webauthn/register/verify", {
        challenge_id: opts.data.challenge_id,
        credential: att,
        label: navigator.platform || "Device",
      });
      toast.success("Passkey added — next sign-in just needs your fingerprint or face.");
      close("added");
    } catch (e) {
      const name = e?.name || "";
      if (name === "NotAllowedError" || name === "AbortError") {
        toast.error("Cancelled. You can add one later from Settings → Passkeys.");
        close("snooze");
      } else {
        toast.error(e?.response?.data?.detail || e?.message || "Couldn't add passkey on this device.");
      }
    } finally {
      setBusy(false);
    }
  };

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 bg-slate-900/60 z-[60] grid place-items-center p-4"
      data-testid="passkey-prompt-modal"
      onClick={() => close("snooze")}
    >
      <div
        className="nb-card p-6 sm:p-8 w-full max-w-md bg-white relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          aria-label="Dismiss"
          onClick={() => close("snooze")}
          data-testid="passkey-prompt-close"
          className="absolute top-3 right-3 p-2 rounded-md hover:bg-slate-100"
        >
          <X className="w-4 h-4" strokeWidth={2.5}/>
        </button>

        <div className="w-14 h-14 bg-yellow-300 border-2 border-slate-900 rounded-md grid place-items-center" style={{ boxShadow: "3px 3px 0 0 #0F172A" }}>
          <Fingerprint className="w-7 h-7" strokeWidth={2.5}/>
        </div>

        <h2 className="font-display text-2xl sm:text-3xl font-black mt-4 leading-tight">
          Sign in faster next time
        </h2>
        <p className="text-sm font-medium text-slate-700 mt-3">
          Add a passkey to this device — use your fingerprint, face, or device PIN instead of Google.
          It's safer than a password and only takes a second.
        </p>

        <ul className="text-xs font-medium text-slate-600 mt-4 space-y-1.5">
          <li className="flex items-start gap-2"><Sparkles className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" strokeWidth={2.5}/> No password to remember</li>
          <li className="flex items-start gap-2"><Sparkles className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" strokeWidth={2.5}/> Works offline on this device</li>
          <li className="flex items-start gap-2"><Sparkles className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" strokeWidth={2.5}/> Phishing-resistant — only works on Studict</li>
        </ul>

        <button
          onClick={addPasskey}
          disabled={busy}
          data-testid="passkey-prompt-add"
          className="nb-btn-primary w-full mt-6 inline-flex items-center justify-center gap-2"
        >
          <Fingerprint className="w-4 h-4" strokeWidth={2.5}/>
          {busy ? "Setting up…" : "Add passkey on this device"}
        </button>

        <div className="flex items-center justify-between mt-4 text-xs">
          <button onClick={() => close("snooze")} data-testid="passkey-prompt-later" className="text-slate-600 font-bold underline hover:no-underline">
            Maybe later
          </button>
          <button onClick={() => close("never")} data-testid="passkey-prompt-never" className="text-slate-400 font-medium hover:text-slate-700">
            Don't ask again
          </button>
        </div>
      </div>
    </div>
  );
}
